import { canonicalizeDomain } from "@/utils/domain";

type PromptDetailsRow = Record<string, any>;

type BrandKeyOptions = {
  brandKeyLabels?: Map<string, string>;
  mainBrandKey?: string;
  resolveBrandKey?: (value?: string | null, isMain?: boolean) => string;
  selectedBrandKeys?: Set<string>;
};

const normalizeText = (value?: unknown) => String(value ?? "").trim();

const normalizeKey = (value?: unknown) => normalizeText(value).toLowerCase();

const decodeHtmlEntities = (value: string) =>
  value
    .replace(/&#x([0-9a-f]+);?/gi, (_match, codePoint: string) => {
      const parsed = Number.parseInt(codePoint, 16);
      return Number.isFinite(parsed) && parsed >= 0 && parsed <= 0x10ffff
        ? String.fromCodePoint(parsed)
        : _match;
    })
    .replace(/&#(\d+);?/g, (_match, codePoint: string) => {
      const parsed = Number.parseInt(codePoint, 10);
      return Number.isFinite(parsed) && parsed >= 0 && parsed <= 0x10ffff
        ? String.fromCodePoint(parsed)
        : _match;
    })
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'");

const stripHtmlToText = (value?: unknown) =>
  decodeHtmlEntities(
    normalizeText(value)
      .replace(/<script[\s\S]*?<\/script>/gi, " ")
      .replace(/<style[\s\S]*?<\/style>/gi, " ")
      .replace(/<br\s*\/?>/gi, " ")
      .replace(/<\/(p|div|li|h[1-6]|tr)>/gi, " ")
      .replace(/<[^>]+>/g, " "),
  )
    .replace(/\s+/g, " ")
    .trim();

const isMentionedFlag = (value: unknown): boolean => {
  if (value === true || value === 1) return true;
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    return (
      normalized === "1" ||
      normalized === "true" ||
      normalized === "t" ||
      normalized === "yes"
    );
  }
  return false;
};

const isMentionSource = (sourceType?: unknown) => {
  const normalized = normalizeKey(sourceType);
  return !normalized || normalized === "mention";
};

const uniqueDelimited = (values: unknown[]) => {
  const seen = new Set<string>();
  const unique: string[] = [];
  values.forEach((value) => {
    const text = normalizeText(value);
    if (!text) return;
    const key = text.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    unique.push(text);
  });
  return unique.join("; ");
};

const getProviderLabel = (row: PromptDetailsRow) =>
  normalizeText(row.provider || row.platform || row.platforms);

const getResponseLabel = (row: PromptDetailsRow) => {
  const status = normalizeKey(row.status);
  if (row.isLoadingRow || ["queued", "running", "snapshot_pending", "processing"].includes(status)) {
    return "In Progress";
  }
  if (status === "not_triggered" || status === "never_run") {
    return "Not Triggered";
  }
  if (status === "failed" || status === "quota_exceeded") {
    return "Failed";
  }
  return stripHtmlToText(row.responseText || row.responseHtml || row.responsePreview);
};

const getMentionPages = (row: PromptDetailsRow) => {
  const pages = Array.isArray(row.citedPages) ? row.citedPages : [];
  return pages.filter((page: PromptDetailsRow) => {
    const type = normalizeText(page.type);
    return (
      ["Competitor", "Owned"].includes(type) &&
      isMentionedFlag(page.isMentioned) &&
      isMentionSource(page.sourceType)
    );
  });
};

const getMentionDomains = (row: PromptDetailsRow) =>
  uniqueDelimited(
    getMentionPages(row)
      .map((page: PromptDetailsRow) => canonicalizeDomain(page.url || ""))
      .filter(Boolean),
  );

const getBrandKey = (
  brand: PromptDetailsRow,
  options: BrandKeyOptions = {},
) => {
  const mainBrandKey = normalizeKey(options.mainBrandKey);
  const fallback = (value?: string | null, isMain?: boolean) =>
    isMain && mainBrandKey ? mainBrandKey : normalizeKey(value);
  return (options.resolveBrandKey || fallback)(
    brand.brandKey || brand.brandDisplay || brand.name || brand.key || "",
    isMentionedFlag(brand.isMain),
  );
};

const brandHasMention = (brand: PromptDetailsRow) => {
  const pages = Array.isArray(brand.citedPages) ? brand.citedPages : [];
  if (!pages.length) {
    const mentions = Number(brand.mentions);
    return Number.isFinite(mentions) && mentions > 0;
  }
  return pages.some(
    (page: PromptDetailsRow) =>
      isMentionedFlag(page.isMentioned) && isMentionSource(page.sourceType),
  );
};

const hasMainBrandMention = (
  row: PromptDetailsRow,
  options: BrandKeyOptions = {},
) => {
  const mainBrandKey = normalizeKey(options.mainBrandKey);
  if (!mainBrandKey) return false;
  const citedBrands = Array.isArray(row.citedBrands) ? row.citedBrands : [];
  return citedBrands.some((brand: PromptDetailsRow) => {
    if (!brandHasMention(brand)) return false;
    return getBrandKey(brand, options) === mainBrandKey;
  });
};

const formatPosition = (value: number) =>
  value.toFixed(1).replace(/\.0$/, "");

const normalizeSearchValue = (value?: unknown) =>
  stripHtmlToText(value)
    .toLowerCase()
    .replace(/\([^)]*\)/g, " ")
    .replace(/[^a-z0-9.]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const getPositionLabel = (
  row: PromptDetailsRow,
  options: BrandKeyOptions = {},
) => {
  if (row.isLoadingRow) return "";

  const mainBrandKey = normalizeKey(options.mainBrandKey);
  if (!mainBrandKey) return normalizeText(row.position);

  const citedBrands = Array.isArray(row.citedBrands) ? row.citedBrands : [];
  const mentionedBrands = citedBrands.filter((brand: PromptDetailsRow) => {
    const key = getBrandKey(brand, options);
    if (!key || !brandHasMention(brand)) return false;
    return !options.selectedBrandKeys || options.selectedBrandKeys.has(key);
  });
  const hasMainMention = mentionedBrands.some(
    (brand: PromptDetailsRow) => getBrandKey(brand, options) === mainBrandKey,
  );
  if (!hasMainMention) return "";

  const responseSearchSpace = ` ${normalizeSearchValue(
    row.responseText || row.responseHtml || row.responsePreview,
  )} `;
  if (responseSearchSpace.trim()) {
    const orderByBrandKey = new Map<string, number>();
    mentionedBrands.forEach((brand: PromptDetailsRow) => {
      const key = getBrandKey(brand, options);
      if (!key) return;

      const candidates = new Set<string>([
        key,
        brand.brandKey || "",
        brand.brandDisplay || "",
        brand.name || "",
        options.brandKeyLabels?.get(key) || "",
      ]);

      let bestIndex = Number.POSITIVE_INFINITY;
      candidates.forEach((candidate) => {
        const normalizedCandidate = normalizeSearchValue(candidate);
        if (!normalizedCandidate || normalizedCandidate.length < 3) return;
        const index = responseSearchSpace.indexOf(` ${normalizedCandidate} `);
        if (index >= 0 && index < bestIndex) {
          bestIndex = index;
        }
      });

      if (!Number.isFinite(bestIndex)) return;
      const existing = orderByBrandKey.get(key);
      if (existing === undefined || bestIndex < existing) {
        orderByBrandKey.set(key, bestIndex);
      }
    });

    const mainIndex = orderByBrandKey.get(mainBrandKey);
    if (mainIndex !== undefined) {
      const rank =
        1 +
        Array.from(orderByBrandKey.values()).filter(
          (index) => index < mainIndex,
        ).length;
      if (rank > 0) return formatPosition(rank);
    }
  }

  const mainMetricPositions = citedBrands
    .filter(
      (brand: PromptDetailsRow) =>
        getBrandKey(brand, options) === mainBrandKey && brandHasMention(brand),
    )
    .map((brand: PromptDetailsRow) => Number(brand.position))
    .filter((position) => Number.isFinite(position) && position > 0);
  if (mainMetricPositions.length) {
    return formatPosition(Math.min(...mainMetricPositions));
  }

  const directPosition = Number(row.position);
  return Number.isFinite(directPosition) && directPosition > 0
    ? formatPosition(directPosition)
    : "";
};

const getSourceMentionedBrands = (row: PromptDetailsRow) => {
  const brands = Array.isArray(row.mentionedBrands) ? row.mentionedBrands : [];
  return uniqueDelimited(
    brands.map(
      (brand: PromptDetailsRow) =>
        brand.name || brand.brandDisplay || brand.key || brand.url,
    ),
  );
};

const getListValue = (value: unknown) => {
  if (Array.isArray(value)) return uniqueDelimited(value);
  return normalizeText(value);
};

export const buildPromptExecutionsExportRows = (
  rows: PromptDetailsRow[],
  options: BrandKeyOptions = {},
) =>
  rows.map((row) => ({
    "Execution Date": normalizeText(row.runDate || row.createdAt || row.created_at),
    Provider: getProviderLabel(row),
    Status: normalizeText(row.status),
    Response: getResponseLabel(row),
    Position: getPositionLabel(row, options),
    Mentions: getMentionDomains(row),
    Mentioned: hasMainBrandMention(row, options) ? "Yes" : "No",
  }));

export const buildPromptSourcesExportRows = (rows: PromptDetailsRow[]) =>
  rows.map((row, index) => ({
    "#": normalizeText(row.rowNumber || index + 1),
    Url: normalizeText(row.url || row.domain),
    Used: normalizeText(row.used),
    Platforms: getListValue(row.platforms ?? row.providers ?? row.provider),
    Type: normalizeText(row.type),
    "Source Type": normalizeText(row.domainType),
    Mentions: getSourceMentionedBrands(row),
    Mentioned: row.mentioned ? "Yes" : "No",
    "Updated At": normalizeText(row.updatedAt),
  }));
